import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { Card, Button, Input, Select, Table, Badge, Modal } from '@/components/ui';
import { Search, UserPlus, Edit2, Trash2, Shield, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
}

const roleOptions = [
  { value: 'staff', label: 'Staff' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'manager', label: 'Manager' },
  { value: 'owner', label: 'Owner' },
];

const roleBadgeVariants: Record<string, 'default' | 'info' | 'warning' | 'success'> = {
  staff: 'default',
  marketing: 'info',
  manager: 'warning',
  owner: 'success',
};

export function UsersPage() {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((state) => state.user);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Only owners can access this page
  const isOwner = currentUser?.role === 'owner';

  const { data: users, isLoading } = useQuery<User[]>({
    queryKey: ['users', { search }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const response = await api.get(`/admin/users?${params}`);
      return response.data?.data?.items || response.data?.data || [];
    },
    enabled: isOwner,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/users/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  if (!isOwner) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Shield className="h-16 w-16 text-gray-600 mb-4" />
        <h2 className="text-xl font-semibold text-white">Access Restricted</h2>
        <p className="text-gray-400 mt-2">Only owners can manage users.</p>
      </div>
    );
  }

  const columns = [
    {
      key: 'user',
      header: 'User',
      render: (user: User) => (
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-midnight-100 flex items-center justify-center">
            <span className="text-lg font-bold text-gold">
              {user.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="font-medium text-white">{user.name}</p>
            <p className="text-sm text-gray-500">{user.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (user: User) => (
        <Badge variant={roleBadgeVariants[user.role] ?? 'default'}>
          {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (user: User) => (
        <Badge variant={user.isActive ? 'success' : 'danger'}>
          {user.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'lastLogin',
      header: 'Last Login',
      render: (user: User) =>
        user.lastLogin ? (
          <span className="text-gray-400">
            {format(new Date(user.lastLogin), 'MMM d, yyyy')}
          </span>
        ) : (
          <span className="text-gray-600">Never</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-24',
      render: (user: User) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEditingUser(user);
              setIsModalOpen(true);
            }}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-midnight-100"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          {user.id !== currentUser?.id && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (confirm(`Delete user "${user.name}"?`)) {
                  deleteMutation.mutate(user.id);
                }
              }}
              className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-midnight-100"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Users</h1>
          <p className="text-gray-400 mt-1">Manage admin users and permissions</p>
        </div>
        <Button
          onClick={() => {
            setEditingUser(null);
            setIsModalOpen(true);
          }}
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Search */}
      <Card padding="md">
        <Input
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<Search className="h-4 w-4" />}
        />
      </Card>

      {/* Users Table */}
      <Card padding="none">
        <Table
          columns={columns}
          data={users ?? []}
          keyField="id"
          loading={isLoading}
          emptyMessage="No users found"
        />
      </Card>

      {/* Create/Edit Modal */}
      <UserModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingUser(null);
        }}
        user={editingUser}
        currentUserId={currentUser?.id}
      />
    </div>
  );
}

// User Modal
interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  currentUserId?: string;
}

function UserModal({ isOpen, onClose, user, currentUserId }: UserModalProps) {
  const queryClient = useQueryClient();
  const isEdit = !!user;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'staff',
    isActive: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
        isActive: user.isActive,
      });
    } else {
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'staff',
        isActive: true,
      });
    }
    setErrors({});
  }, [user]);

  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload = {
        name: data.name,
        email: data.email,
        role: data.role,
        isActive: data.isActive,
        ...(data.password && { password: data.password }),
      };
      if (isEdit) {
        return api.put(`/admin/users/${user.id}`, payload);
      }
      return api.post('/admin/users', { ...payload, password: data.password });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: (error: Error & { response?: { data?: { errors?: Record<string, string> } } }) => {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    // Basic validation
    if (!formData.name.trim()) {
      setErrors({ name: 'Name is required' });
      return;
    }
    if (!formData.email.trim()) {
      setErrors({ email: 'Email is required' });
      return;
    }
    if (!isEdit && !formData.password) {
      setErrors({ password: 'Password is required' });
      return;
    }

    saveMutation.mutate(formData);
  }

  const isOwnAccount = user?.id === currentUserId;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit User' : 'Add User'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {isOwnAccount && (
          <div className="flex items-center gap-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20 px-4 py-3 text-sm text-yellow-400">
            <AlertTriangle className="h-4 w-4" />
            <span>You are editing your own account</span>
          </div>
        )}

        <Input
          label="Name"
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          error={errors.name}
          required
        />

        <Input
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
          error={errors.email}
          required
        />

        <Input
          label={isEdit ? 'New Password' : 'Password'}
          type="password"
          value={formData.password}
          onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
          error={errors.password}
          helper={isEdit ? 'Leave blank to keep current password' : undefined}
          required={!isEdit}
        />

        <Select
          label="Role"
          options={roleOptions}
          value={formData.role}
          onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))}
          disabled={isOwnAccount}
        />

        {!isOwnAccount && (
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
              className="w-4 h-4 rounded border-gray-600 bg-midnight-100 text-gold focus:ring-gold"
            />
            <div>
              <span className="text-white font-medium">Active</span>
              <p className="text-sm text-gray-400">Allow this user to log in</p>
            </div>
          </label>
        )}

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-midnight-100">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={saveMutation.isPending}>
            {isEdit ? 'Update User' : 'Create User'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
